<template>
  <div class="description">
    Select which schools this event will apply to.
    <br />
    You can select all schools or choose specific schools.
    <br />
    You can always change this later.
  </div>
  <div class="content">
    <v-checkbox
      class="custom-input custom-checkbox mt-3"
      label="Select All Schools"
      v-model="wizard.selectAllSchools"
      :disabled="wizard.loading"
      data-cy="wizard-select-all-schools"
      @update:model-value="handleSelectAllChange"
    ></v-checkbox>
    <v-select
      v-if="!wizard.selectAllSchools"
      v-model="wizard.selectedSchools"
      :items="schoolOptions"
      item-title="school_name"
      item-value="school_id"
      label="Select Schools"
      multiple
      chips
      closable-chips
      :disabled="wizard.loading || loading"
      :loading="loading"
      data-cy="wizard-schools-select"
      class="mt-4"
      bg-color="white"
    ></v-select>
  </div>
</template>

<script setup lang="ts">
import useWizard from "@/stores/school/wizard";
import { useDistrictStore } from "@/stores/district";
import { useSchoolStore } from "@/stores/school";
import { ref, onMounted } from "vue";
import type { GetSchoolDetails } from "@/api_types/school";

const wizard = useWizard();
const districtStore = useDistrictStore();
const schoolStore = useSchoolStore();
const loading = ref(false);
const schoolOptions = ref<GetSchoolDetails[]>([]);

const handleSelectAllChange = (value: boolean) => {
  if (value) {
    // Clear selected schools when "select all" is checked
    wizard.selectedSchools = [];
  }
};

// Helper function to check if a school is a district school
const isDistrictSchool = (isDistrict: string | boolean | undefined): boolean => {
  if (isDistrict === undefined) return false;
  return isDistrict === "yes" || isDistrict === true || isDistrict === "true";
};

const fetchSchools = async () => {
  loading.value = true;
  try {
    const schools = await districtStore.fetchSchools();
    // Filter out district schools (is_district === "yes" or is_district === true)
    const nonDistrictSchools = schools.filter(
      (school) => !isDistrictSchool(school.is_district as string | boolean)
    );
    
    // Include the current school if it exists and is not a district school
    if (schoolStore.school?.school_id) {
      const currentSchoolIsDistrict = isDistrictSchool(
        schoolStore.school.is_district as string | boolean
      );
      
      if (!currentSchoolIsDistrict) {
        schoolOptions.value = [
          {
            school_id: schoolStore.school.school_id,
            school_name: schoolStore.school.school_name || "Current School"
          } as GetSchoolDetails,
          ...nonDistrictSchools
        ];
        
        // Set default to current school if no schools are selected
        if (wizard.selectedSchools.length === 0 && !wizard.selectAllSchools) {
          wizard.selectedSchools = [schoolStore.school.school_id];
        }
      } else {
        schoolOptions.value = nonDistrictSchools;
      }
    } else {
      schoolOptions.value = nonDistrictSchools;
    }
  } catch (error) {
    console.error("Error fetching schools:", error);
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchSchools();
});
</script>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;
@use "@/assets/_breakpoints.scss" as breakpoints;

.description {
  text-align: center;
  font-size: large;
  min-width: min-content;
}

.content {
  margin-top: 20px;
  width: 100%;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}
</style>

